/**
 * External-board API client (Phase 24 KAN-01 / KAN-04).
 *
 * IMPORTANT: this module DOES NOT route through services/api/client.ts. Doing
 * so would clear localStorage.monksheets_auth_token on any 401 (see
 * client.ts:28), which would log out the internal Google-SSO user whenever
 * an external visitor hits /api/projects/:id/board without a session. We use
 * fetch() directly with credentials:'include' so the HttpOnly external session
 * cookie is sent, but no internal token state is touched.
 *
 * Phase 23 Landmine #8 — same isolation contract as externalProjects.ts.
 */
import { ExternalApiError } from './externalAuth';
import type { BoardResponse } from './board';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

async function externalFetch<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // External-portal audience hint so authenticateAny resolves the external
      // session even when an internal session also exists in the browser.
      'X-Audience': 'external',
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    type ErrorPayload = { error?: string; message?: string };
    const payload = (await res.json().catch(() => ({}))) as ErrorPayload;
    throw new ExternalApiError({
      statusCode: res.status,
      errorCode: typeof payload.error === 'string' ? payload.error : undefined,
      message:
        typeof payload.message === 'string'
          ? payload.message
          : (payload.error ?? 'Unknown error'),
    });
  }
  return (await res.json()) as T;
}

export async function fetchBoardExternal(projectId: string): Promise<BoardResponse> {
  const res = await externalFetch<{ data: BoardResponse }>('GET', `/projects/${projectId}/board`);
  return res.data;
}

/** External member row (bod 2 reassign target picker). */
export interface ExternalMember {
  /** MKS-132 — 'user' rows are internal members (names only, email null). */
  kind: 'user' | 'external_user';
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

/**
 * External tasks v1 (260702) — members of the actor's project, used to
 * populate the bod 2 reassign picker. MKS-132: the roster now also contains
 * internal members (kind='user', names only). Cookie creds; external-gated on
 * the backend. Includes the actor; the picker filters out the current
 * assignee.
 */
export async function fetchProjectExternalMembers(
  projectId: number,
): Promise<ExternalMember[]> {
  const res = await externalFetch<{ data: ExternalMember[] }>(
    'GET',
    `/projects/${projectId}/external/members`,
  );
  return res.data;
}

/**
 * External tasks v1 (260702) bod 2 — an external member of the project
 * reassigns a task. MKS-132: the target may be an external member
 * (kind='external_user') or an internal user (kind='user'); the body carries
 * exactly one of externalAssigneeId / assigneeId accordingly. Cookie creds
 * only (same isolation contract as fetchBoardExternal).
 */
export async function reassignExternalTask(
  taskId: number,
  target: { kind: 'user' | 'external_user'; id: number },
): Promise<void> {
  await externalFetch<{ data: unknown }>(
    'PATCH',
    `/tasks/${taskId}/external-assignee`,
    target.kind === 'user' ? { assigneeId: target.id } : { externalAssigneeId: target.id },
  );
}

/**
 * External tasks v1 (260702) bod 3 — the current external assignee approves or
 * returns-for-fix. Reassigns the task to the internal PM server-side.
 */
export async function submitExternalReview(
  taskId: number,
  decision: 'approved' | 'rejected',
  reason?: string,
): Promise<void> {
  await externalFetch<{ data: unknown }>('POST', `/tasks/${taskId}/external-review`, {
    decision,
    ...(reason ? { reason } : {}),
  });
}

/**
 * Quick task 260810-l18 — the external AUTHOR of a finished request (portal)
 * reopens it. Cookie creds only (same isolation contract as fetchBoardExternal).
 * Task 1 wires 'reopened' only; Task 3 widens the decision union to include
 * 'approved' for the shared AuthorReviewActions component.
 */
export async function submitAuthorReviewExternal(
  taskId: number,
  decision: 'approved' | 'reopened',
  reason?: string,
): Promise<void> {
  await externalFetch<{ data: unknown }>('POST', `/tasks/${taskId}/author-review`, {
    decision,
    ...(reason ? { reason } : {}),
  });
}

// ---------------------------------------------------------------------------
// Watcher notifications v1 (260702) — external self-watch. Same cookie-only
// isolation contract as the rest of this module (never touches the internal
// client.ts token/auth state). Backend routes: /tasks/:id/external-watchers.
// ---------------------------------------------------------------------------

/** Watcher row as returned by the external watchers list endpoint. */
export interface ExternalWatcherDto {
  taskId: number;
  userId: number | null;
  externalUserId: number | null;
  kind: 'user' | 'external_user';
  createdAt: string;
  user: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
  };
}

export async function fetchTaskWatchersExternal(
  taskId: number,
): Promise<ExternalWatcherDto[]> {
  const res = await externalFetch<{ data: ExternalWatcherDto[] }>(
    'GET',
    `/tasks/${taskId}/external-watchers`,
  );
  return res.data;
}

export async function selfWatchTaskExternal(taskId: number): Promise<void> {
  await externalFetch<{ data: { success: boolean } }>(
    'POST',
    `/tasks/${taskId}/external-watchers/me`,
  );
}

export async function selfUnwatchTaskExternal(taskId: number): Promise<void> {
  await externalFetch<void>('DELETE', `/tasks/${taskId}/external-watchers/me`);
}

// ---------------------------------------------------------------------------
// External acceptance v1 (260702) — external users VIEW + EDIT acceptance
// criteria. Same cookie-only isolation contract (never touches internal
// client.ts token/auth state). Backend: /tasks/:id/external-acceptance.
// ---------------------------------------------------------------------------

/** Acceptance item shape — identical to the internal client's AcceptanceItem. */
export interface ExternalAcceptanceItem {
  id: string;
  text: string;
  done: boolean;
}

export async function fetchAcceptanceExternal(
  taskId: number,
): Promise<ExternalAcceptanceItem[]> {
  const res = await externalFetch<{ data: ExternalAcceptanceItem[] }>(
    'GET',
    `/tasks/${taskId}/external-acceptance`,
  );
  return res.data;
}

export async function addAcceptanceItemExternal(
  taskId: number,
  text: string,
): Promise<ExternalAcceptanceItem> {
  const res = await externalFetch<{ data: ExternalAcceptanceItem }>(
    'POST',
    `/tasks/${taskId}/external-acceptance`,
    { text },
  );
  return res.data;
}

export async function patchAcceptanceItemExternal(
  taskId: number,
  itemId: string,
  patch: { done: boolean } | { text: string },
): Promise<ExternalAcceptanceItem> {
  const res = await externalFetch<{ data: ExternalAcceptanceItem }>(
    'PATCH',
    `/tasks/${taskId}/external-acceptance/${itemId}`,
    patch,
  );
  return res.data;
}

export async function deleteAcceptanceItemExternal(
  taskId: number,
  itemId: string,
): Promise<void> {
  await externalFetch<void>(
    'DELETE',
    `/tasks/${taskId}/external-acceptance/${itemId}`,
  );
}
